function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useState, useCallback, useRef } from 'react';
import styled from 'styled-components/native';
import _ from 'lodash';
import PropTypes from 'prop-types';
import useMobile from '../hooks/use-mobile';
import Spacer from '../spacer';
import Colors from '../colors';
import Text from '../text';
import Icon from '../icon';
import SliderBar from './slider';
import Currency from '../utilities/currency';
const TYPES = ['default', 'edit', 'minMax'];

const useToggle = function () {
  let initialValue = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
  const [newValue, setValue] = useState(initialValue);
  const toggle = useCallback(() => {
    setValue(value => !value);
  }, []);
  return [newValue, toggle];
};

const formatAmount = (value, decimal) => {
  const amount = _.toNumber(value);

  const decimals = {
    minimumFractionDigits: decimal,
    maximumFractionDigits: decimal
  };
  return amount.toLocaleString('en-US', decimals);
};

const getRoundedAmount = (amount, step) => {
  const remainder = amount % step;
  return remainder ? amount - remainder + step : amount;
};

const StyledContainer = styled.View`
  width: 100%;
`;
const StyledGroup = styled.View`
  flex-direction: row;
  justify-content: ${props => props.minMaxLabel ? 'space-between' : 'center'};
`;
const StyledCurrency = styled.Text`
  font-size: ${props => props.mobile ? 16 : 32}px;
  font-family: 'LarsseitRegular';
  color: ${Colors.gray600};
  line-height: ${props => props.mobile ? 170 : 160}%;
`;
const StyledHeight = styled.View`
  height: ${props => props.mobile ? props.height[0] : props.height[1]}px;
  width: 0;
`;
const StyledTextInput = styled.TextInput`
  margin: -5px;
  padding: ${props => props.mobile ? 2 : -5}px;
  background: transparent;
  border: transparent;
  font-family: 'LarsseitBold';
  font-size: ${props => props.mobile ? 29 : 49}px;
  line-height: ${props => props.mobile ? 34.8 : 58.8}px;
  color: ${Colors.gray600};
  text-align: center;
  width: ${props => props.length}ch;
`;

const StyledIcon = icon => {
  const iconStyle = {
    lineHeight: icon.mobile ? 32 : 58.8
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Spacer, {
    size: "f8",
    width: true
  }), /*#__PURE__*/React.createElement(Icon.MaterialIcons, {
    name: "edit",
    size: icon.mobile ? 18 : 20,
    color: Colors.gray100,
    onPress: icon.onPress,
    style: iconStyle
  }));
};

const StyledLabel = label => {
  if (label.overTransactionLimit) {
    return /*#__PURE__*/React.createElement(Text.BodyExtraSmall, {
      weight: "regular",
      color: Colors.gray600,
      align: "center"
    }, "You can advance up to", ' ', /*#__PURE__*/React.createElement(Text.BodyExtraSmall, {
      weight: "regular",
      color: Colors.advanceBlue400
    }, Currency.pesoFormat(label.maxCreditLimit)), ",", ' ', "with a maximum disbursement amount of", ' ', Currency.pesoFormat(label.upperLimit), ' ', "per request.");
  }

  return /*#__PURE__*/React.createElement(Text.BodyExtraSmall, {
    weight: "regular",
    color: Colors.gray600,
    align: "center"
  }, "You can advance up to", ' ', /*#__PURE__*/React.createElement(Text.BodyExtraSmall, {
    weight: "regular",
    color: Colors.advanceBlue400
  }, Currency.pesoFormat(label.maxCreditLimit)));
};

const StyledMinMaxLabel = label => /*#__PURE__*/React.createElement(StyledGroup, {
  minMaxLabel: true
}, /*#__PURE__*/React.createElement(Text.BodyExtraSmall, {
  weight: "regular",
  color: Colors.gray600
}, "\u20B1", ' ', formatAmount(label.minCreditLimit, 0)), /*#__PURE__*/React.createElement(Text.BodyExtraSmall, {
  weight: "regular",
  color: Colors.gray600
}, "\u20B1", ' ', formatAmount(label.maxCreditLimit, 0)));

export default function RequestAmount(props) {
  var _props$upperLimit;

  const ref = useRef();
  const mobile = useMobile();
  const {
    valueSetter,
    hideSlider
  } = props;
  const maxValue = (_props$upperLimit = props.upperLimit) !== null && _props$upperLimit !== void 0 ? _props$upperLimit : props.maxCreditLimit;
  const [focus, setFocus] = useState(false);
  const [input, showInput] = useToggle();
  const [value, setValue] = useState(props.value || maxValue);
  const [amount, setAmount] = useState(formatAmount(value, 2));
  const type = TYPES.includes(props.type) ? props.type : RequestAmount.defaultProps.type;
  const inputOnFocus = {
    outline: 'none'
  };

  const onValueChange = newValue => {
    let newAmount = _.toNumber(newValue);

    switch (true) {
      case newAmount < props.minCreditLimit || !newAmount:
        newAmount = props.minCreditLimit;
        break;

      case newAmount > maxValue:
        newAmount = maxValue;
        break;

      default:
        if (!input) newAmount = getRoundedAmount(newAmount, props.step);
        break;
    }

    setValue(newAmount);
    valueSetter(newAmount);
    setAmount(formatAmount(newAmount, 2));
    showInput();
  };

  const onSliderValueChange = newValue => {
    const newAmount = Number.isNaN(newValue) ? value : newValue;
    setValue(newAmount);
    valueSetter(newAmount);
    setAmount(formatAmount(newAmount, 2));
  };

  return /*#__PURE__*/React.createElement(StyledContainer, null, /*#__PURE__*/React.createElement(Text.BodyExtraSmall, {
    weight: "medium",
    color: Colors.gray400,
    align: "center"
  }, "How much do you need?"), /*#__PURE__*/React.createElement(Spacer, {
    size: "f8",
    height: true
  }), /*#__PURE__*/React.createElement(StyledGroup, null, /*#__PURE__*/React.createElement(StyledCurrency, {
    mobile: mobile
  }, "\u20B1"), /*#__PURE__*/React.createElement(Spacer, {
    size: mobile ? 'f4' : 'f8',
    width: true
  }), !input ? /*#__PURE__*/React.createElement(Text.H2, {
    color: Colors.gray600
  }, formatAmount(value, 2)) : /*#__PURE__*/React.createElement(StyledTextInput, {
    ref: ref,
    length: amount.length,
    mobile: mobile,
    autoFocus: true,
    value: amount.replace(/[^0-9.]/g, ''),
    onChangeText: setAmount,
    keyboardType: "number-pad",
    onBlur: () => onValueChange(amount.replace(/[^0-9.]/g, '')),
    onFocus: () => setFocus(true),
    style: focus && inputOnFocus
  }), type !== 'default' && /*#__PURE__*/React.createElement(StyledIcon, {
    mobile: mobile,
    onPress: showInput
  })), !hideSlider && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StyledHeight, {
    mobile: mobile,
    height: [4, 11]
  }), /*#__PURE__*/React.createElement(SliderBar, {
    value: _.toNumber(value),
    minAmount: props.minCreditLimit,
    maxAmount: props.maxCreditLimit,
    upperLimit: props.upperLimit,
    step: props.step,
    onChange: newValue => onSliderValueChange(newValue)
  }), /*#__PURE__*/React.createElement(Spacer, {
    size: "f8",
    height: true
  }), type === 'minMax' && /*#__PURE__*/React.createElement(StyledMinMaxLabel, _extends({
    mobile: mobile
  }, props)) || /*#__PURE__*/React.createElement(StyledLabel, _extends({
    mobile: mobile
  }, props))), /*#__PURE__*/React.createElement(StyledHeight, {
    mobile: mobile,
    height: [1, 6]
  }));
}
RequestAmount.defaultProps = {
  type: 'default',
  minCreditLimit: 0,
  maxCreditLimit: 1000,
  upperLimit: 1000,
  step: 100,
  value: null,
  valueSetter: () => {},
  hideSlider: false,
  overTransactionLimit: false
};
RequestAmount.propTypes = {
  type: PropTypes.oneOf(TYPES),
  minCreditLimit: PropTypes.number,
  maxCreditLimit: PropTypes.number,
  upperLimit: PropTypes.number,
  step: PropTypes.number,
  value: PropTypes.number,
  valueSetter: PropTypes.func,
  hideSlider: PropTypes.bool,
  overTransactionLimit: PropTypes.bool
};
//# sourceMappingURL=request-amount.js.map